import React from "react";
import { Box, Button, Paper, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { SimpleTextInput } from "components/FormTextInput";
import SimpleSwitch from "components/Switch";
import TranslationButtons from "components/TranslationButtons";
import { SimpleSelectInput } from "components/FormSelectInput";
import { Save } from "@mui/icons-material";

const useStyles = makeStyles((theme) => ({
  switchGridContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  switchContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
  },
  maintenanceContainer: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: "#f8f9fa",
    borderRadius: theme.spacing(1),
    border: "1px solid #e9ecef",
  },
  sectionSubtitle: {
    marginBottom: theme.spacing(2),
    fontWeight: "500",
    color: "#555555",
  },
  whiteBackgroundInput: {
    backgroundColor: "#ffffff",
    borderRadius: theme.spacing(0.5),
  },
  sectionSaveButton: {
    marginTop: theme.spacing(2),
    backgroundColor: "#4caf50",
    color: "white",
    fontWeight: "bold",
    "&:hover": {
      backgroundColor: "#45a049",
    },
    "&:disabled": {
      backgroundColor: "#e0e0e0",
      color: "#9e9e9e",
    },
  },
}));

const FeaturesSettings = ({
  operatorDetails,
  onChange,
  handleOpenModal,
  onSave,
  loading,
  canEditReconciliation = false,
}) => {
  const styles = useStyles();

  return (
    <Paper
      elevation={1}
      sx={{
        p: 3,
        mb: 3,
        borderRadius: 1,
        backgroundColor: "#ffffff",
        border: "1px solid #e0e0e0",
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontWeight: "600",
          color: "#333333",
          mb: 3,
          borderBottom: "2px solid #f0f0f0",
          pb: 1,
        }}
      >
        Features & Settings
      </Typography>

      <Box className={styles.switchGridContainer}>
        <Box className={styles.switchContainer}>
          <SimpleSwitch
            legendText={"Status"}
            name="isActive"
            value={operatorDetails.isActive}
            label={operatorDetails.isActive ? "Active" : "Inactive"}
            onChange={onChange}
          />
        </Box>

        <Box className={styles.switchContainer}>
          <SimpleSwitch
            legendText="Coin Purchase Allowed"
            name="coinPurchaseAllowed"
            value={operatorDetails.coinPurchaseAllowed}
            label={operatorDetails.coinPurchaseAllowed ? "Active" : "Inactive"}
            onChange={onChange}
          />
        </Box>

        <Box className={styles.switchContainer}>
          <SimpleSwitch
            legendText="Show Wallet in Nav Bar?"
            name="showWalletIcon"
            value={operatorDetails.showWalletIcon}
            label={operatorDetails.showWalletIcon ? "YES" : "NO"}
            onChange={onChange}
          />
        </Box>

        <Box className={styles.switchContainer}>
          <SimpleSwitch
            legendText="Under Maintenance"
            name="isUnderMaintenance"
            value={operatorDetails.isUnderMaintenance}
            label={operatorDetails.isUnderMaintenance ? "YES" : "NO"}
            onChange={onChange}
          />
        </Box>

        <Box className={styles.switchContainer}>
          <SimpleSwitch
            legendText="Show Win/Loss?"
            name="showWinLoss"
            value={operatorDetails.showWinLoss}
            label={operatorDetails.showWinLoss ? "YES" : "NO"}
            onChange={onChange}
          />
        </Box>

        <Box className={styles.switchContainer}>
          <SimpleSwitch
            legendText="Show RTP Game Rules?"
            name="showRtpGameRules"
            value={operatorDetails.showRtpGameRules}
            label={operatorDetails.showRtpGameRules ? "YES" : "NO"}
            onChange={onChange}
          />
        </Box>

        <Box className={styles.switchContainer}>
          <SimpleSwitch
            legendText="Show New FTUE?"
            name="showNewFtue"
            value={operatorDetails.showNewFtue}
            label={operatorDetails.showNewFtue ? "YES" : "NO"}
            onChange={onChange}
          />
        </Box>

        <Box className={styles.switchContainer}>
          <SimpleSwitch
            legendText="Show Tutorial Video?"
            name="isTutorialVideoEnabled"
            value={operatorDetails.isTutorialVideoEnabled}
            label={operatorDetails.isTutorialVideoEnabled ? "YES" : "NO"}
            onChange={onChange}
          />
        </Box>

        <Box className={styles.switchContainer}>
          <SimpleSwitch
            legendText="Show Sticky CTA?"
            name="isStickyCtaEnabled"
            value={operatorDetails.isStickyCtaEnabled}
            label={operatorDetails.isStickyCtaEnabled ? "YES" : "NO"}
            onChange={onChange}
          />
        </Box>

        <Box className={styles.switchContainer}>
          <SimpleSwitch
            legendText="Show Odds Calculating Animation?"
            name="showOddsCalculatingAnimation"
            value={operatorDetails.showOddsCalculatingAnimation}
            label={operatorDetails.showOddsCalculatingAnimation ? "YES" : "NO"}
            onChange={onChange}
          />
        </Box>

        <Box className={styles.switchContainer}>
          <SimpleSwitch
            legendText="Start Match Button Restriction"
            name="startMatchBtnRestriction"
            value={operatorDetails.startMatchBtnRestriction}
            label={operatorDetails.startMatchBtnRestriction ? "YES" : "NO"}
            onChange={onChange}
          />
        </Box>

        {canEditReconciliation && (
          <Box className={styles.switchContainer}>
            <SimpleSwitch
              legendText="Allow Manual Reconciliation"
              name="allowManualReconciliation"
              value={operatorDetails.allowManualReconciliation}
              label={operatorDetails.allowManualReconciliation ? "YES" : "NO"}
              onChange={onChange}
            />
          </Box>
        )}

        <Box className={styles.switchContainer}>
          <SimpleSwitch
            legendText="Division Base OVR Enabled"
            name="divisionBaseOvrEnabled"
            value={operatorDetails.divisionBaseOvrEnabled}
            label={operatorDetails.divisionBaseOvrEnabled ? "YES" : "NO"}
            onChange={onChange}
          />
        </Box>
      </Box>

      {/* Start Match Button Div Dropdown */}
      {operatorDetails?.startMatchBtnRestriction && (
        <Box sx={{ mt: 2 }}>
          <SimpleSelectInput
            label="Start Match Button Div"
            name="startMatchBtnDiv"
            value={operatorDetails.startMatchBtnDiv || ""}
            onChange={onChange}
            placeholdertext="Select Div (1-10)"
            options={[
              { id: 1, label: "1" },
              { id: 2, label: "2" },
              { id: 3, label: "3" },
              { id: 4, label: "4" },
              { id: 5, label: "5" },
              { id: 6, label: "6" },
              { id: 7, label: "7" },
              { id: 8, label: "8" },
              { id: 9, label: "9" },
              { id: 10, label: "10" },
            ]}
          />
        </Box>
      )}

       {/* Matches For Effect - only relevant when Division Base OVR is disabled */}
      {operatorDetails?.divisionBaseOvrEnabled === false && (
        <Box sx={{ mt: 2 }}>
          <SimpleTextInput
            label="Matches For Effect (Division Base OVR)"
            name="matchesForDivisionBaseOvr"
            type="number"
            value={
              operatorDetails.matchesForDivisionBaseOvr === null ||
              operatorDetails.matchesForDivisionBaseOvr === undefined
                ? ""
                : operatorDetails.matchesForDivisionBaseOvr
            }
            onChange={onChange}
            placeholdertext="Matches the division base OVR stays active before bypass"
          />
        </Box>
      )}

      {/* Maintenance Text Field */}
      {operatorDetails?.isUnderMaintenance && (
        <Box className={styles.maintenanceContainer}>
          <Typography variant="body2" className={styles.sectionSubtitle}>
            Maintenance Configuration
          </Typography>
          <SimpleTextInput
            label="Maintenance Text"
            name="underMaintenanceTitle"
            value={operatorDetails.underMaintenanceTitle}
            onChange={onChange}
            button={
              <TranslationButtons
                field="underMaintenanceTitleTrans"
                onClick={handleOpenModal}
              />
            }
            className={styles.whiteBackgroundInput}
          />
        </Box>
      )}

      {/* Section Save Button */}
      {onSave && (
        <Box display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            onClick={onSave}
            disabled={loading}
            startIcon={<Save />}
            className={styles.sectionSaveButton}
          >
            {loading ? "Saving..." : "Save Features"}
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default FeaturesSettings;
